/**
 * What students do.
 *
 * ── WHY THIS SECTION EXISTS ──────────────────────────────────────────────
 * Thomas, Sep 8 2026: "students can get ahead and find successful solutions
 * for their career goals with P3 even without a mentor." Every earlier draft
 * of this dashboard framed the whole cohort against matching, which capped
 * the product's value at mentor capacity and made every unmatched student
 * read as a failure. This section is the evidence for the other half of the
 * product: what a student does in the app from day one.
 *
 * ── WHAT A LIVE TENANT SEES (Sep 11 2026) ────────────────────────────────
 * The record totals are measured: questions asked, mentor video plays,
 * videos saved, mentors followed, and the students with none of these on
 * record. They are running totals on each student's own record, read hourly
 * with the rest of the snapshot, so they include what a student did before
 * naming the institution, and the footnotes say so. From the app's analytics
 * come app use and applications through the app over the last 90 days, for
 * students whose analytics profile names the institution, so those figures
 * are a floor; opening an opportunity is not counted per institution yet.
 * The question records add questions by term and the share a mentor
 * answered. Pulse AI and pathways are not counted per institution yet: one
 * card says so in the snapshot's words, never a zero and never the sample's
 * figures. While app activity itself is not counted (the analytics are not
 * configured, or the snapshot is older), one card says so for all of them.
 *
 * ── THE HONESTY GATE ─────────────────────────────────────────────────────
 * Every figure here is a count of events by a GROUP of students, so it
 * publishes only when that group clears ten, and a count of students is
 * withheld when the students it leaves out are fewer than ten. On a first
 * cohort several cards sit in their threshold state for a while. That state
 * names the floor, a measurement that has not started, not a broken panel.
 * It shows the distance to the floor only where that distance is safe to
 * show: a live tenant's withheld figure arrives with no count at all, so it
 * draws no meter rather than a false zero.
 *
 * ── WHAT IS NOT CLAIMED ──────────────────────────────────────────────────
 * P3 knows a student applied through the app; it does not know the result.
 * The footnotes say so, and the insight line never states a cause.
 */

import {
  PulseMark,
  PulseWatermark,
  QuestionGlyph,
  } from "~/app/components/marks";
import {
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  Chip,
  Denominator,
  LeadFigure,
  Minor,
  ProvenanceNote,
  SectionHead,
  StateCard,
} from "~/app/components/primitives";
import { SECTION_LEDE, SECTION_TITLE } from "~/app/lib/admin-nav";
import { formatCount } from "~/app/lib/format";
import type { ActivityFigure, CohortView } from "~/app/lib/demo-data";
import { RankedList, SERIES } from "~/app/panels/panel-charts";
import type { Ratio } from "~/lib/app-use";
import { K } from "~/lib/suppression";

/**
 * The threshold state every card in this section shares. The meter shows
 * the distance to the floor only where it is known and safe: a real zero,
 * or the sample. A live tenant's withheld figure arrives with no count (the
 * Worker sends none under the floor), so it draws no meter.
 */
function Floor({
  figure,
  noun,
  title,
  children,
}: {
  figure: ActivityFigure;
  noun: string;
  title: string;
  children: React.ReactNode;
}) {
  const known = figure.students.kind === "value" || figure.studentsRaw > 0;
  return (
    <StateCard
      variant="building"
      title={title}
      progress={known ? { have: figure.studentsRaw, need: K, noun } : undefined}
    >
      {children}
    </StateCard>
  );
}

/** A figure the last refresh could not read: the source's own sentence, or the record totals'. */
function Unread({ children }: { children?: React.ReactNode }) {
  return (
    <StateCard variant="not-measured" title="Not read on the last refresh">
      {children ?? "P3 could not read this figure from student records on the last refresh."}
    </StateCard>
  );
}

/** An institution the app's analytics hold no student for: not a zero, and not a failure. */
function NotInAnalytics({ what }: { what: string }) {
  return (
    <StateCard variant="not-measured" title="Not in the app's analytics under your name yet">
      None of your students appears under your institution's name in the app's analytics yet, so
      P3 cannot count their {what}. It starts once their profiles carry your name.
    </StateCard>
  );
}

/** Sessions per active student, one decimal. */
const formatSessions = (v: Ratio): string => v.ratio.toFixed(1);

/** An average session in minutes and seconds: "6 min 52 s". */
function formatSessionLength(v: Ratio): string {
  const total = Math.round(v.ratio);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes > 0 ? `${minutes} min ${seconds} s` : `${seconds} s`;
}

export function ActivityPanel({ view }: { view: CohortView }) {
  const { activity, institution } = view;

  /* A live tenant whose record totals are not computed yet (no snapshot, or
     one written before Sep 11 2026): the section head and one sentence, and
     no card that could read as a zero or borrow the sample's figures. */
  if (!activity.recordsMeasured) {
    return (
      <section className="e-sec e-print-page">
        <SectionHead
          id="engagement"
          title={SECTION_TITLE.engagement}
          lede={SECTION_LEDE.engagement}
        />
        <StateCard variant="not-measured" title="Not measured per institution yet">
          {activity.notMeasured ?? "P3 has not computed your figures yet."}
        </StateCard>
      </section>
    );
  }

  const videos = activity.videoPlays;
  const idle = activity.notYetActive;
  const { appUse, answers } = activity;
  const opportunitiesPublish = activity.opportunitiesApplied.total.kind === "value";
  const questionsPublish = activity.questions.total.kind === "value";
  const pulsePublish = activity.pulse.total.kind === "value";
  // A live tenant counts applications and not the opens (Sep 11 2026); the
  // sample demonstrates both.
  const live = activity.liveTenant === true;
  const where = `at ${institution.legalName}, ${view.windowLabel}`;

  return (
    <section className="e-sec e-print-page">
      <SectionHead
        id="engagement"
        title={SECTION_TITLE.engagement}
        lede={SECTION_LEDE.engagement}
      />

      <div className="e-grid">
        <Card span={2}>
          <CardHeader>
            <CardTitle sub="Advice and answers from P3 mentors, played and saved in the app.">
              Mentor videos
            </CardTitle>
          </CardHeader>
          <CardBody>
            {videos.total.kind === "value" ? (
              <div className="e-vstack">
                <LeadFigure
                  cell={videos.total}
                  format={formatCount}
                  label="Mentor video plays"
                  provenance={activity.videosProvenance}
                />
                <div className="e-minor-row">
                  <Minor
                    cell={activity.videosSaved.total}
                    format={formatCount}
                    label="Videos saved"
                  />
                  <Minor
                    cell={activity.mentorsFollowed.total}
                    format={formatCount}
                    label="Mentors followed"
                  />
                  <Minor
                    cell={activity.mentorsFollowed.students}
                    format={formatCount}
                    label="Students following one"
                  />
                </div>
                <Denominator
                  cell={videos.share}
                  of="of your students have played a mentor video"
                  noun="students"
                  label="Students who played one"
                />
              </div>
            ) : videos.total.kind === "unavailable" ? (
              <Unread />
            ) : (
              <Floor
                figure={videos}
                noun="students who played one"
                title="Published at ten students who played one"
              >
                A play count is a count of the students behind it, so it
                publishes once ten of your students have played a mentor
                video.
              </Floor>
            )}
          </CardBody>
          <CardFootnote>
            <p className="e-note">
              Running totals since each student joined P3, including before
              they named you.
            </p>
            <ProvenanceNote provenance={activity.videosProvenance} />
          </CardFootnote>
        </Card>

        <Card solo className="e-card-art">
          <CardHeader
            aside={
              <span className="e-kpi-tile hue-2" aria-hidden="true">
                <QuestionGlyph />
              </span>
            }
          >
            <CardTitle sub="Asked of P3 mentors, directly or in the open pool.">
              Questions asked
            </CardTitle>
          </CardHeader>
          <CardBody>
            {/* No watermark here since Sep 18 2026 (Thomas): the bubble left. */}
            {questionsPublish ? (
              <div className="e-vstack">
                <LeadFigure
                  cell={activity.questions.total}
                  format={formatCount}
                  label="Questions asked"
                  provenance={activity.questionsProvenance}
                />
                <div className="e-minor-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
                  <Minor
                    cell={activity.questions.students}
                    format={formatCount}
                    label="Students who asked"
                  />
                  {answers.notMeasured ? null : (
                    <Minor
                      cell={answers.asked.total}
                      format={formatCount}
                      label="Last 90 days"
                      unavailableNote={answers.unread}
                    />
                  )}
                </div>
                {answers.notMeasured ? null : (
                  <Denominator
                    cell={answers.answered}
                    of="of their questions have a mentor's answer"
                    noun="questions"
                    label="Questions answered"
                  />
                )}
              </div>
            ) : activity.questions.total.kind === "unavailable" ? (
              <Unread />
            ) : (
              <Floor
                figure={activity.questions}
                noun="students who asked"
                title="Published at ten asking students"
              >
                A question count is a count of the students behind it, so it
                publishes once ten of your students have asked one.
              </Floor>
            )}
          </CardBody>
          <CardFootnote>
            <p className="e-note">
              P3 shows you counts, never the text of a question. Answered
              means a mentor replied, in writing or with a video.
            </p>
            <ProvenanceNote provenance={activity.questionsProvenance} />
          </CardFootnote>
        </Card>
      </div>

      <div className="e-grid">
        {activity.notMeasured ? (
          /* A live tenant whose app activity is not counted per institution:
             one card in the snapshot's words, not four empty ones. */
          <Card span={2}>
            <CardHeader>
              <CardTitle>Opportunities, Pulse AI and pathways</CardTitle>
            </CardHeader>
            <CardBody>
              <StateCard variant="not-measured" title="Not counted per institution yet">
                {activity.notMeasured}
              </StateCard>
            </CardBody>
          </Card>
        ) : (
          <Card span={2}>
            <CardHeader>
              <CardTitle
                sub={
                  live
                    ? `Scholarships, internships and jobs applied to through the app, ${appUse.span}.`
                    : "Scholarships, internships and jobs, opened and applied to in the app."
                }
              >
                Opportunities
              </CardTitle>
            </CardHeader>
            <CardBody>
              {opportunitiesPublish ? (
                <div className="e-vstack">
                  <LeadFigure
                    cell={activity.opportunitiesApplied.total}
                    format={formatCount}
                    label="Opportunities applied to"
                    provenance={activity.provenance}
                  />
                  {live ? (
                    <div className="e-minor-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
                      <Minor
                        cell={activity.opportunitiesApplied.students}
                        format={formatCount}
                        label="Students who applied"
                      />
                      <Minor
                        cell={activity.opportunitiesOpened.total}
                        format={formatCount}
                        label="Opportunities opened"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="e-minor-row">
                        <Minor
                          cell={activity.opportunitiesOpened.total}
                          format={formatCount}
                          label="Opportunities opened"
                        />
                        <Minor
                          cell={activity.opportunitiesApplied.students}
                          format={formatCount}
                          label="Students who applied"
                        />
                        <Minor
                          cell={activity.opportunitiesOpened.students}
                          format={formatCount}
                          label="Students who opened one"
                        />
                      </div>
                      <Denominator
                        cell={activity.applyRate}
                        of="of students who opened an opportunity went on to apply"
                        noun="students who opened one"
                        label="Application rate"
                      />
                    </>
                  )}
                </div>
              ) : appUse.unread ? (
                <Unread>{appUse.unread}</Unread>
              ) : activity.opportunitiesApplied.total.kind === "unavailable" ? (
                <NotInAnalytics what="applications" />
              ) : (
                <Floor
                  figure={activity.opportunitiesApplied}
                  noun="students who applied"
                  title="Opportunity figures begin at ten applying students"
                >
                  Applications are counted by the students who made them, so the
                  figure publishes once ten of your students have applied to
                  something through the app. Until then it is held, not zero.
                </Floor>
              )}
            </CardBody>
            <CardFootnote>
              <p className="e-note">
                {live
                  ? "P3 records the application, not its result. A student counts only when their analytics profile names you."
                  : "P3 records the application, not its result."}
              </p>
              <ProvenanceNote provenance={activity.provenance} />
            </CardFootnote>
          </Card>
        )}

        <Card solo>
          <CardHeader>
            <CardTitle sub="No question, video, save, follow, mentor or request on record.">
              Not yet active
            </CardTitle>
          </CardHeader>
          <CardBody>
            {idle.kind === "value" ? (
              <div className="e-vstack">
                <LeadFigure
                  cell={idle}
                  format={formatCount}
                  label="Students not yet active"
                  provenance={activity.notYetActiveProvenance}
                />
                <Denominator
                  cell={activity.notYetActiveShare}
                  of="of your students"
                  noun="students"
                  label="Share not yet active"
                />
              </div>
            ) : idle.kind === "suppressed" ? (
              <StateCard variant="building" title="Withheld under the reporting floor">
                This count, or the students it leaves out, is a group of fewer
                than ten, so it is not shown.
              </StateCard>
            ) : (
              <Unread />
            )}
          </CardBody>
          <CardFootnote>
            <p className="e-note">P3 never tells you which students these are.</p>
            <ProvenanceNote provenance={activity.notYetActiveProvenance} />
          </CardFootnote>
        </Card>
      </div>

      {activity.notMeasured ? null : (
        <div className="e-grid">
          <Card span={2}>
            <CardHeader>
              <CardTitle sub={`Students who opened the P3 app ${appUse.span}, and how they used it.`}>
                Active in the app
              </CardTitle>
            </CardHeader>
            <CardBody>
              {appUse.active.kind === "value" ? (
                <div className="e-vstack">
                  <LeadFigure
                    cell={appUse.active}
                    format={formatCount}
                    label={`Students active ${appUse.span}`}
                    provenance={appUse.provenance}
                  />
                  <div className="e-minor-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
                    <Minor
                      cell={appUse.sessionsPerStudent}
                      format={formatSessions}
                      label="Sessions per active student"
                    />
                    <Minor
                      cell={appUse.averageSessionSeconds}
                      format={formatSessionLength}
                      label="Average session"
                    />
                  </div>
                  <Denominator
                    cell={appUse.activeShare}
                    of={`of your students opened the app ${appUse.span}`}
                    noun="students"
                    label="Share active in the app"
                  />
                </div>
              ) : appUse.unread ? (
                <Unread>{appUse.unread}</Unread>
              ) : appUse.active.kind === "unavailable" ? (
                <NotInAnalytics what="app use" />
              ) : (
                <StateCard variant="building" title="Withheld under the reporting floor">
                  This count, or the students it leaves out, is a group of fewer
                  than ten, so it is not shown.
                </StateCard>
              )}
            </CardBody>
            <CardFootnote>
              <p className="e-note">
                A floor: a student counts only when their analytics profile names you.
              </p>
              <ProvenanceNote provenance={appUse.provenance} />
            </CardFootnote>
          </Card>

          {activity.pulseNotMeasured ? (
            /* Pulse AI and pathways, not counted per institution yet: one card
               in the snapshot's words, not two empty ones. */
            <Card solo>
              <CardHeader>
                <CardTitle>Pulse AI and pathways</CardTitle>
              </CardHeader>
              <CardBody>
                <StateCard variant="not-measured" title="Not counted per institution yet">
                  {activity.pulseNotMeasured}
                </StateCard>
              </CardBody>
            </Card>
          ) : (
            <Card solo className="e-card-pulse e-card-art">
              <CardHeader
                aside={
                  <>
                    <Chip tone="neutral">18 and over</Chip>
                    <PulseMark />
                  </>
                }
              >
                <CardTitle sub="Conversations with the career coach inside the app.">
                  Pulse AI
                </CardTitle>
              </CardHeader>
              <CardBody>
                <PulseWatermark />
                {pulsePublish ? (
                  <div className="e-vstack">
                    <LeadFigure
                      cell={activity.pulse.total}
                      format={formatCount}
                      label="Pulse AI sessions"
                      provenance={activity.pulseProvenance}
                    />
                    <div className="e-minor-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
                      <Minor
                        cell={activity.pulse.students}
                        format={formatCount}
                        label="Students using it"
                      />
                      {/* A live tenant's Pulse figures come from the web
                          store, which counts sessions and the students who had
                          them and holds no age. The sample demonstrates the
                          adult count; a live tenant omits a figure it does not
                          have rather than printing "Not available" in it. */}
                      {activity.adults.kind === "unavailable" ? null : (
                        <Minor
                          cell={activity.adults}
                          format={formatCount}
                          label="Students 18 and over"
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <Floor
                    figure={activity.pulse}
                    noun="adult students using it"
                    title="Published at ten adult students"
                  >
                    Pulse AI is offered to students 18 and over, and the session
                    count publishes once ten of them have used it.
                  </Floor>
                )}
              </CardBody>
              <CardFootnote>
                <p className="e-note">
                  Sessions are counted. Their content is never read.
                </p>
                <ProvenanceNote provenance={activity.pulseProvenance} />
              </CardFootnote>
            </Card>
          )}
        </div>
      )}

      {activity.notMeasured || activity.pathwaysNotMeasured ? null : (
        <div className="e-grid">
          <Card span="full">
            <CardHeader>
              <CardTitle sub="Career pathways opened in the guide. Interest, not a decision.">
                Pathways explored
              </CardTitle>
            </CardHeader>
            <CardBody>
              {activity.pathwaysExplored.allSuppressed ? (
                <StateCard variant="building" title="No pathway has reached ten students yet">
                  Every pathway is under the reporting threshold on its own. The
                  list fills in as your students explore.
                </StateCard>
              ) : (
                <RankedList
                  distribution={activity.pathwaysExplored}
                  label={`Career pathways opened by students ${where}`}
                  color={SERIES[3]}
                />
              )}
            </CardBody>
            <CardFootnote>
              <p className="e-note">{activity.pathwaysExplored.footnote}</p>
              <ProvenanceNote
                provenance={activity.pathwaysProvenance}
                suppressed={activity.pathwaysExplored.hiddenCount > 0}
              />
            </CardFootnote>
          </Card>
        </div>
      )}
    </section>
  );
}
